'use client';

import { useEffect, useState } from 'react';
import AdminShell from '@/components/AdminShell';
import { StatCard, PageHeader, Card, Badge, Table, Tone } from '@/components/ui';
import { useI18n } from '@/i18n';
import { dashboardApi } from '@/lib/api';

interface PendingItem {
  id: string;
  typeKey: string;
  target: string;
  time: string;
  tone: Tone;
}

interface AlertItem {
  id: string;
  levelKey: 'high' | 'medium' | 'low';
  message: string;
  time: string;
  tone: Tone;
}

interface RecentOp {
  time: string;
  user: string;
  action: string;
  tone: Tone;
}

// ===== 后端接口返回结构（字段名以 backend routes/core.ts 为准）=====
interface DashboardStats {
  pendingApprovals: number;
  alerts: number;
  yesterdayOutput: number;
  fundInOut: { in: number; out: number };
  ordersTodo: number;
  kycTodo: number;
  depositsTodo: number;
  withdrawalsTodo: number;
}

interface TodoOrder { id: string; userId?: string; type?: string; amount?: string | number; currency?: string; createdAt?: string | null; }
interface TodoKyc { id: string; userId?: string; companyName?: string | null; docNumber?: string | null; createdAt?: string | null; }
interface TodoFund { id: string; userId?: string; amount?: string | number; currency?: string; createdAt?: string | null; }
interface TodoEquity { id: string; userId?: string; units?: number | null; shareValue?: string | number; createdAt?: string | null; }
interface RawAlert { id: string; level?: string; message?: string; createdAt?: string | null; }
interface RawAuditLog { id: string; adminId?: string | null; module?: string | null; action?: string | null; createdAt?: string | null; }

const fmtTime = (d?: string | null): string => (d ? d.slice(5, 16).replace('T', ' ') : '—');
const fmtNum = (n: string | number | null | undefined): string => Number(n ?? 0).toLocaleString('en-US', { maximumFractionDigits: 2 });
const shortId = (id?: string | null): string => (id ? (id.length > 8 ? id.slice(0, 8) : id) : '—');

// 后端订单类型 → dashboard.pt* i18n 键后缀（transfer 对应 pt-equity = Equity Transfer）
const todoTypeKey = (raw?: string): string => {
  if (raw === 'transfer') return 'equity';
  return 'subscription';
};

// 最近操作 tone：按 action 语义着色
const opTone = (action?: string | null): Tone => {
  const a = action ?? '';
  if (/reject|freeze|blacklist|handle/.test(a)) return 'danger';
  if (/approve|create|sync|unfreeze/.test(a)) return 'success';
  if (/settle|generate/.test(a)) return 'gold';
  return 'info';
};

export default function DashboardPage() {
  const { t } = useI18n();

  const [pending, setPending] = useState<PendingItem[]>([]);
  const [alerts, setAlerts] = useState<AlertItem[]>([]);
  const [recentOps, setRecentOps] = useState<RecentOp[]>([]);
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    // 待办统计
    dashboardApi
      .stats()
      .then((s) => setPendingCount((s as unknown as DashboardStats).pendingApprovals ?? 0))
      .catch(() => undefined);
    // 待办明细
    dashboardApi
      .todos()
      .then((res) => {
        const todos = res as unknown as { orders: TodoOrder[]; kyc: TodoKyc[]; deposits: TodoFund[]; withdrawals: TodoFund[]; equities: TodoEquity[] };
        const items: PendingItem[] = [
          ...(todos.orders ?? []).map((o): PendingItem => ({ id: o.id, typeKey: todoTypeKey(o.type), target: `${shortId(o.userId)} · $${fmtNum(o.amount)} ${o.currency ?? ''}`, time: fmtTime(o.createdAt), tone: 'gold' })),
          ...(todos.kyc ?? []).map((k): PendingItem => ({ id: k.id, typeKey: 'kyc', target: k.companyName || k.docNumber || shortId(k.userId), time: fmtTime(k.createdAt), tone: 'info' })),
          ...(todos.deposits ?? []).map((d): PendingItem => ({ id: d.id, typeKey: 'deposit', target: `${shortId(d.userId)} · $${fmtNum(d.amount)} ${d.currency ?? ''}`, time: fmtTime(d.createdAt), tone: 'gold' })),
          ...(todos.withdrawals ?? []).map((w): PendingItem => ({ id: w.id, typeKey: 'withdraw', target: `${shortId(w.userId)} · $${fmtNum(w.amount)} ${w.currency ?? ''}`, time: fmtTime(w.createdAt), tone: 'warning' })),
          ...(todos.equities ?? []).map((e): PendingItem => ({ id: e.id, typeKey: 'equity', target: `${shortId(e.userId)} · ${e.units ?? 1} × $${fmtNum(e.shareValue)}`, time: fmtTime(e.createdAt), tone: 'danger' })),
        ];
        setPending(items);
      })
      .catch(() => undefined);
    // 告警
    dashboardApi
      .alerts()
      .then((res) => {
        const list = res as unknown as RawAlert[];
        setAlerts(
          (list ?? []).map((a) => {
            const level: AlertItem['levelKey'] = a.level === 'high' || a.level === 'medium' || a.level === 'low' ? a.level : 'medium';
            return { id: a.id, levelKey: level, message: a.message ?? '', time: fmtTime(a.createdAt), tone: level === 'high' ? 'danger' : level === 'medium' ? 'warning' : 'info' };
          })
        );
      })
      .catch(() => undefined);
    // 最近操作（审计日志）
    dashboardApi
      .recentOps()
      .then((res) => {
        const list = res as unknown as RawAuditLog[];
        setRecentOps(
          (list ?? []).map((l) => ({ time: fmtTime(l.createdAt), user: l.adminId ? shortId(l.adminId) : '—', action: `${l.module ?? ''} · ${l.action ?? ''}`, tone: opTone(l.action) }))
        );
      })
      .catch(() => undefined);
  }, []);

  const levelLabel = (k: AlertItem['levelKey']) => (k === 'high' ? t('dashboard.alertHigh') : k === 'medium' ? t('dashboard.alertMedium') : t('dashboard.alertLow'));

  // 告警级别对应的左侧色条与徽章颜色
  const levelBar: Record<AlertItem['levelKey'], string> = {
    high: 'border-l-danger',
    medium: 'border-l-warning',
    low: 'border-l-info',
  };

  return (
    <AdminShell>
      <div className="space-y-6 animate-fade-in">
        <PageHeader
          title={t('dashboard.title')}
          subtitle={t('dashboard.subtitle')}
          actions={<span className="px-3 py-1.5 rounded-lg bg-white/15 text-white text-sm font-semibold border border-white/20 backdrop-blur-sm">{t('dashboard.dataAsOf')}</span>}
        />

        {/* Core stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label={t('dashboard.statAssetValue')} value="$2.8B" sub={t('dashboard.statAssetSub')} color="gold" icon="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          <StatCard label={t('dashboard.statPlatformInvestment')} value="$486M" sub={t('dashboard.statPlatformSub')} color="success" icon="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
          <StatCard label={t('dashboard.statPending')} value={String(pendingCount)} sub={t('dashboard.statPendingSub')} color="warning" icon="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a1 1 0 001 1h4a1 1 0 001-1M9 5a1 1 0 000 1h4a1 1 0 001-1m-6 9h6m-6 4h6" />
          <StatCard label={t('dashboard.statDistribution')} value="$412K" sub={t('dashboard.statDistributionSub')} color="info" icon="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <Card title={t('dashboard.pendingTitle')} actions={<span className="text-xs text-maple-text">{t('dashboard.pendingTotal')}</span>}>
              <Table headers={[t('dashboard.colId'), t('dashboard.colType'), t('dashboard.colContent'), t('dashboard.colTime'), t('dashboard.colStatus')]}>
                {pending.map((p) => (
                  <tr key={p.id} className="hover:bg-hover transition-colors">
                    <td className="px-6 py-3.5 text-sm font-mono text-foreground font-medium">{p.id}</td>
                    <td className="px-6 py-3.5 text-sm font-semibold text-foreground">{t(`dashboard.pt${p.typeKey}`)}</td>
                    <td className="px-6 py-3.5 text-sm text-secondary">{p.target}</td>
                    <td className="px-6 py-3.5 text-sm text-muted">{p.time}</td>
                    <td className="px-6 py-3.5">
                      <Badge tone={p.tone}>{t('dashboard.pendingBadge')}</Badge>
                    </td>
                  </tr>
                ))}
              </Table>
            </Card>
          </div>

          <div className="space-y-6">
            <Card title={t('dashboard.alertTitle')} actions={<span className="w-2 h-2 rounded-full bg-danger animate-pulse"></span>}>
              <div className="divide-y divide-border">
                {alerts.map((a) => (
                  <div key={a.id} className={`px-6 py-3.5 flex items-start gap-3 border-l-4 ${levelBar[a.levelKey]}`}>
                    <Badge tone={a.tone}>{levelLabel(a.levelKey)}</Badge>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-foreground">{a.message}</div>
                      <div className="text-xs text-muted mt-0.5">{a.time}</div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            <Card title={t('dashboard.recentTitle')}>
              <div className="divide-y divide-border">
                {recentOps.map((op, i) => (
                  <div key={i} className="px-6 py-3.5 flex items-start gap-3">
                    <div className="w-7 h-7 rounded-full bg-hover flex items-center justify-center flex-shrink-0 ring-1 ring-inset ring-border">
                      <span className="text-[10px] font-semibold text-foreground">{op.user.split(' ')[0]}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-secondary">{op.action}</div>
                      <div className="text-xs text-muted mt-0.5">{op.user} · {op.time}</div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>
      </div>
    </AdminShell>
  );
}
